import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Col, Container, FormControl, InputGroup, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap'
import styled from 'styled-components'
import { FaCopy, FaDownload, FaPlusCircle, FaSearch, FaTrash } from 'react-icons/fa'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import InfiniteScroll from 'react-infinite-scroll-component'
import { toast } from 'react-toastify'
import {
  Button,
  Form,
  FormGroup,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap'
import { ToastOptionsDefault } from 'utils/toastOptions'
import { FaEye, FaEyeSlash } from 'react-icons/fa6'

const GalleryContainer = styled(Container)`
  margin-top: 2rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  overflow: hidden;
`

const ScreenshotCard = styled(Card)`
  margin-bottom: 1rem;
  cursor: pointer;
`

const ToggleButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  gap: 10px;
`

const MyGalleryPage: React.FC = () => {
  const [screenshots, setScreenshots] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1) // Initial page for fetching data
  const [hasMore, setHasMore] = useState(true) // Flag to indicate if more pages exist
  const [isFetching, setIsFetching] = useState(false) // Single flag for fetching state
  const [total, setTotal] = useState(0)
  const [isBlurred, setIsBlurred] = useState(false) // State for controlling image blur
  const [isDownloading, setIsDownloading] = useState(false) // State for handling download process
  const navigate = useNavigate()
  const urlRef = useRef('')
  const [selectedScreenshotId, setSelectedScreenshotId] = useState<string | null>(null)
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [downloadModal, setDownloadModal] = useState<boolean>(false)
  const [searchTerm, setSearchTerm] = useState<string>('')
  
  const toggleDeleteModal = () => setDeleteModal(!deleteModal)
  const toggleDownloadModal = () => setDownloadModal(!downloadModal)
  
  const toggleBlur = () => {
    setIsBlurred(!isBlurred)
  }
  
  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const response = await fetch('/api/screenshot/download-gallery', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      
      if (!response.ok) {
        throw new Error('Failed to generate download')
      }
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'ScreenMe-gallery.zip') // File name
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link) // Clean up
      
      toast.success('Download will start shortly!', ToastOptionsDefault)
    } catch (err) {
      toast.error('Failed to download gallery. Please retry later.', ToastOptionsDefault)
    } finally {
      setIsDownloading(false)
      toggleDownloadModal()
    }
  }
  
  // See https://stackoverflow.com/questions/73002902/api-getting-called-twice-in-react
  // This problem appears only in dev mode.
  const fetchMyScreenshots = useCallback(async () => {
    if (isFetching || !hasMore) return
    
    setIsFetching(true)
    
    try {
      const response = await fetch(`/api/screenshot/my?page=${page}&limit=10`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      
      if (!response.ok) {
        if (response.status === 404) {
          const data = await response.json()
          if (data.message === 'No screenshots found for this user') {
            setHasMore(false)
          }
        } else {
          toast.error('Failed to retrieve screenshots. Please retry later.', ToastOptionsDefault)
        }
      } else {
        const data = await response.json()
        setScreenshots((prevScreenshots) => [...prevScreenshots, ...data.screenshots])
        setHasMore(data.currentPage < data.totalPages)
        setPage((prevPage) => prevPage + 1)
        setTotal(data.total)
      }
    } catch (err) {
      toast.error('Failed to retrieve screenshots. Please retry later.', ToastOptionsDefault)
      setError('Failed to fetch screenshots')
    } finally {
      setIsFetching(false)
    }
  }, [page, hasMore, isFetching])
  
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      urlRef.current = 'http://localhost:3000/'
    }
    
    if (!isFetching) {
      fetchMyScreenshots()
    }
  }, [])
  
  const handlePrivacyToggle = async (id: string, privacy: string) => {
    try {
      const response = await fetch(`/api/screenshot/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ privacy: privacy === 'public' ? 'private' : 'public' }),
      })
      
      if (!response.ok) {
        toast.error('Failed to update privacy. Please retry later.', ToastOptionsDefault)
      } else {
        const updatedScreenshot = await response.json()
        setScreenshots((prevScreenshots) =>
          prevScreenshots.map((screenshot) =>
            screenshot.id === updatedScreenshot.id ? updatedScreenshot : screenshot
          )
        )
        toast.success(
          `Screenshot privacy has successfully been changed to ${privacy === 'public' ? 'private' : 'public'}.`,
          ToastOptionsDefault
        )
      }
    } catch (err) {
      setError('Failed to update privacy')
    }
  }
  
  const handleDelete = async (id: string) => {
    try {
      toggleDeleteModal()
      const response = await fetch(`/api/screenshot/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      
      if (!response.ok) {
        throw new Error('Failed to delete screenshot')
      }
      
      setScreenshots((prevScreenshots) => prevScreenshots.filter((screenshot) => screenshot.id !== id))
      setTotal((prevTotal) => prevTotal - 1)
      
      toast.warning('Screenshot has been successfully deleted.', ToastOptionsDefault)
    } catch (err) {
      setError('Failed to delete screenshot')
    }
  }
  
  const filteredScreenshots = screenshots.filter(screenshot =>
    screenshot.title.toLowerCase().includes(searchTerm.toLowerCase())
  )
  
  if (error) {
    return <div className="alert alert-danger">{error}</div>
  }
  
  return (
    <>
      <div className="position-relative" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <section className="section section-hero section-shaped" style={{ flexGrow: 1 }}>
          <div className="shape shape-style-1 shape-default">
            <span className="span-150" />
            <span className="span-50" />
            <span className="span-50" />
            <span className="span-75" />
            <span className="span-100" />
            <span className="span-75" />
            <span className="span-50" />
            <span className="span-100" />
            <span className="span-50" />
            <span className="span-100" />
          </div>
          <GalleryContainer id="galleryContainer">
            <Row className="align-items-center justify-content-between">
              <Col xs="auto" className="d-flex">
                <h1>My Gallery ({total} screenshots)</h1>
              </Col>
              <Col xs="auto" className="d-flex justify-content-center flex-grow-1">
                <Form className="navbar-search navbar-search-dark form-inline">
                  <FormGroup className="mb-0">
                    <InputGroup className="input-group-alternative">
                      <InputGroupText>
                        <FaSearch />
                      </InputGroupText>
                      <FormControl
                        placeholder="Search by name"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </FormGroup>
                </Form>
              </Col>
              <Col xs="auto" className="d-flex justify-content-end">
                <Button color="success" className="mr-2" href="/upload">
                  <FaPlusCircle /> Upload
                </Button>
                <Button color="primary" onClick={(e) => {
                  e.stopPropagation()
                  toggleDownloadModal()
                }}>
                  <FaDownload /> Download
                </Button>
                <Button onClick={toggleBlur} color="warning" outline className="ml-2">
                  {isBlurred ? <FaEye /> : <FaEyeSlash />}
                </Button>
              </Col>
            </Row>
            
            <div className="mt-2">
              { filteredScreenshots.length > 0 ? (
                <InfiniteScroll
                  dataLength={screenshots.length}
                  next={fetchMyScreenshots}
                  hasMore={hasMore}
                  loader={
                    <div className="d-flex justify-content-center">
                      <div className="spinner-wrapper">
                        <Spinner animation="border" role="status" className="custom-spinner">
                          <span className="sr-only">Loading...</span>
                        </Spinner>
                        <div className="loading-text">Loading...</div>
                      </div>
                    </div>
                  }
                  endMessage={ <p style={ { textAlign: 'center' } }>Yay ! You have seen it all.</p> }
                >
                  <Row>
                    { filteredScreenshots.map((screenshot) => (
                      <Col md={4} key={screenshot.id}>
                        <ScreenshotCard>
                          <Card.Img
                            onClick={() => navigate(`/${screenshot.url}`)}
                            color="top"
                            src={`${urlRef.current}${screenshot.path}`}
                            alt={screenshot.title}
                            className={isBlurred ? 'tw-blur-md' : ''}
                          />
                          <Card.Body>
                            <Card.Title>{screenshot.title}</Card.Title>
                            <Card.Text>Views: {screenshot.views}</Card.Text>
                            <Card.Text>
                              <i>
                                {new Date(screenshot.createdAt).toLocaleString('en-EN', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </i>
                            </Card.Text>
                            <ToggleButtonContainer>
                              <CopyToClipboard text={`https://screen-me.cloud/${screenshot.url}`}>
                                <Button
                                  color="primary"
                                  className="mr-2"
                                  onClick={() => {
                                    toast.success('Screenshot URL copied to your clipboard!', ToastOptionsDefault)
                                  }}
                                >
                                  <FaCopy />
                                </Button>
                              </CopyToClipboard>
                              <ToggleButtonGroup
                                type="radio"
                                name={`privacyOptions-${screenshot.id}`}
                                value={screenshot.privacy}
                                onChange={() => handlePrivacyToggle(screenshot.id, screenshot.privacy)}
                              >
                                <ToggleButton
                                  id={`public-${screenshot.id}`}
                                  color="outline-secondary"
                                  value="public"
                                >
                                Public
                                </ToggleButton>
                                <ToggleButton
                                  id={`private-${screenshot.id}`}
                                  color="outline-secondary"
                                  value="private"
                                >
                                Private
                                </ToggleButton>
                              </ToggleButtonGroup>
                              <Button
                                color="danger"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedScreenshotId(screenshot.id)
                                  toggleDeleteModal()
                                }}
                              >
                                <FaTrash />
                              </Button>
                            </ToggleButtonContainer>
                          </Card.Body>
                        </ScreenshotCard>
                      </Col>
                    ))}
                  </Row>
                </InfiniteScroll>
              ): (
                <p style={ { textAlign: 'center' } }>No screenshots found matching your search.</p>
              ) }
            </div>
          </GalleryContainer>
          <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
            <ModalHeader toggle={toggleDeleteModal}>
              <h2>Confirm Delete Screenshot</h2>
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to delete this screenshot ?</p>
            </ModalBody>
            <ModalFooter>
              <Button
                color="danger"
                onClick={() => {
                  if (selectedScreenshotId) {
                    handleDelete(selectedScreenshotId)
                  }
                }}
              >
                Confirm Delete
              </Button>{' '}
              <Button onClick={toggleDeleteModal} outline>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>
          <Modal isOpen={downloadModal} toggle={toggleDownloadModal}>
            <ModalHeader toggle={toggleDownloadModal}>
              <h2>Download your gallery</h2>
            </ModalHeader>
            <ModalBody>
              {!isDownloading ? (
                <p>Do you wish to download your gallery ? It will be saved in a ZIP archive, and the process may take some time.</p>
              ) : (
                <div className="text-center">
                  <Spinner animation="border" role="status" className="mb-3">
                    <span className="sr-only">Loading...</span>
                  </Spinner>
                  <p>Please keep this window open. Your download will start shortly...</p>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                color="success"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download'}
              </Button>{' '}
              <Button onClick={toggleDownloadModal} outline>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>
          <div className="separator separator-bottom separator-skew zindex-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
            >
              <polygon className="fill-white" points="2560 0 2560 100 0 100" />
            </svg>
          </div>
        </section>
      </div>
    </>
  )
}

export default MyGalleryPage
